import { gql } from 'graphql-request'
import { CollectibleStatusCardProps, CollectibleStatus } from 'app/components/my-collectibles/CollectibleStatusCard'

export type UserCollectible = CollectibleStatusCardProps & {
  paymentsThisQuarter?: number
  paymentsNeeded?: number
}

export type UserCollectiblesData = {
  isSyncing: boolean
  isEligibleForCollectibles: boolean
  collectibles: UserCollectible[]
  currentQuarter: number
  paymentsThisQuarter: number
  paymentsNeeded: number
  planType?: string
}

// Raw GraphQL response types
interface RawCollectible {
  id: string
  title: string
  description: string
  quarter: string
  status?: string
  isPurchasable?: boolean | null
  image?: string | null
  redeemedDate?: string | null
  unlockDate?: string | null
  shopifyUrl?: string | null
  availableFrom?: string | null
  availableUntil?: string | null
}

interface RawUserCollectibles {
  isSyncing?: boolean | null
  isEligibleForCollectibles?: boolean | null
  currentQuarter?: number | null
  paymentsThisQuarter?: number | null
  paymentsNeeded?: number | null
  planType?: string | null
  collectibles?: RawCollectible[] | null
}

export interface RawUserCollectiblesData {
  userCollectibles?: RawUserCollectibles | null
}

// Helper function to get current quarter
const getCurrentQuarter = () => {
  const currentDate = new Date()
  const currentMonth = currentDate.getMonth() + 1
  return Math.ceil(currentMonth / 3)
}

// Default values for UserCollectiblesData
export const defaultUserCollectiblesData: UserCollectiblesData = {
  isSyncing: false,
  isEligibleForCollectibles: false,
  collectibles: [],
  currentQuarter: getCurrentQuarter(),
  paymentsThisQuarter: 0,
  paymentsNeeded: 3,
  planType: undefined,
}

export const createUserCollectiblesQuery = () => gql`
  query getUserCollectibles {
    userCollectibles {
      isSyncing
      isEligibleForCollectibles
      currentQuarter
      paymentsThisQuarter
      paymentsNeeded
      planType
      collectibles {
        id
        title
        description
        quarter
        status
        isPurchasable
        image
        redeemedDate
        unlockDate
        shopifyUrl
        availableFrom
        availableUntil
      }
    }
  }
`

export const mapToUserCollectibles = (data: RawUserCollectiblesData | null): UserCollectiblesData => {
  if (!data?.userCollectibles) {
    return defaultUserCollectiblesData
  }

  const rawData = data.userCollectibles

  return {
    isSyncing: rawData.isSyncing ?? defaultUserCollectiblesData.isSyncing,
    isEligibleForCollectibles: rawData.isEligibleForCollectibles ?? defaultUserCollectiblesData.isEligibleForCollectibles,
    collectibles: rawData.collectibles?.map((item: RawCollectible) => ({
      id: item.id,
      title: item.title,
      description: item.description,
      quarter: item.quarter,
      image: item.image || '',
      status: (item.status as CollectibleStatus) || 'locked',
      isPurchasable: item.isPurchasable ?? null,
      redeemedDate: item.redeemedDate ?? null,
      shopifyUrl: item.shopifyUrl ?? null,
      unlockDate: item.unlockDate ?? null,
      availableFrom: item.availableFrom ?? null,
      availableUntil: item.availableUntil ?? null,
    })) ?? defaultUserCollectiblesData.collectibles,
    currentQuarter: rawData.currentQuarter ?? defaultUserCollectiblesData.currentQuarter,
    paymentsThisQuarter: rawData.paymentsThisQuarter ?? defaultUserCollectiblesData.paymentsThisQuarter,
    paymentsNeeded: rawData.paymentsNeeded ?? defaultUserCollectiblesData.paymentsNeeded,
    planType: rawData.planType ?? defaultUserCollectiblesData.planType,
  }
}
